import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Eye,
  KeyRound,
  Wallet,
  ShoppingBag,
  X,
  Plus,
  Minus,
} from "lucide-react";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showBalance, setShowBalance] = useState(false);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [balanceAmount, setBalanceAmount] = useState("");

  const load = async () => {
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openDetails = async (user) => {
    setSelectedUser(user);
    setShowDetails(true);
    setOrders([]);

    try {
      const res = await api.get(`/admin/users/${user.id}/orders`);
      setOrders(res.data || []);
    } catch (err) {
      setOrders([]);
    }
  };

  const changePassword = async () => {
    if (!selectedUser) return;

    if (newPassword.length < 6) {
      toast.error("Şifre en az 6 karakter olmalı.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Şifreler eşleşmiyor.");
      return;
    }

    try {
      await api.post(
        `/admin/users/${selectedUser.id}/change-password`,
        {
          new_password: newPassword,
          new_password_confirm: confirmPassword,
        }
      );

      toast.success("Kullanıcı şifresi değiştirildi.");
      setNewPassword("");
      setConfirmPassword("");
      setShowPassword(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const adjustBalance = async (sign) => {
    if (!selectedUser) return;

    const amount = parseFloat(balanceAmount);

    if (!amount || amount <= 0) {
      toast.error("Geçerli bir tutar gir.");
      return;
    }

    const delta = sign === "add" ? amount : -amount;

    try {
      await api.post(
        `/admin/users/${selectedUser.id}/adjust-balance?delta=${delta}`
      );

      toast.success(
        sign === "add"
          ? `₺${amount.toFixed(2)} bakiye eklendi.`
          : `₺${amount.toFixed(2)} bakiye çıkarıldı.`
      );

      setBalanceAmount("");
      setShowBalance(false);
      await load();

      const res = await api.get("/admin/users");
      const updated = res.data.find((u) => u.id === selectedUser.id);

      if (updated) {
        setSelectedUser(updated);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const formatDate = (date) => {
    if (!date) return "—";

    try {
      return new Date(date).toLocaleString("tr-TR");
    } catch {
      return "—";
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-8">

        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1">
            Yönetim
          </div>

          <h1 className="font-display font-bold text-2xl text-white">
            Kullanıcılar
          </h1>

          <p className="text-sm text-white/40 mt-1">
            Üyeleri ve hesap hareketlerini yönetin.
          </p>
        </div>

        <div className="border border-white/10 bg-[hsl(0,0%,7%)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-white/40 border-b border-white/10">
              <tr>
                <th className="text-left py-4 px-5 font-bold">Kullanıcı</th>
                <th className="text-left px-4 font-bold">Rol</th>
                <th className="text-right px-4 font-bold">Bakiye</th>
                <th className="text-center px-4 font-bold">Sipariş</th>
                <th className="text-left px-4 font-bold">Kayıt Tarihi</th>
                <th className="text-right px-5 font-bold">İşlem</th>
              </tr>
            </thead>

            <tbody data-testid="admin-users-table">
              {users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-white/40">
                    Kullanıcı bulunamadı.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-white/5 hover:bg-white/[0.025] transition-colors"
                  >
                    <td className="py-4 px-5">
                      <div className="font-mono font-semibold text-white">
                        {u.username}
                      </div>
                      <div className="text-[10px] text-white/30 mt-1">
                        ID: {u.id}
                      </div>
                    </td>

                    <td className="px-4">
                      <span
                        className={`text-[10px] uppercase tracking-wider border px-2 py-1 ${
                          u.role === "admin"
                            ? "text-[hsl(221,83%,63%)] border-[hsl(221,83%,53%)]/40"
                            : "text-white/60 border-white/20"
                        }`}
                      >
                        {u.role || "user"}
                      </span>
                    </td>

                    <td className="px-4 text-right">
                      <span className="font-mono font-bold text-[hsl(142,71%,55%)]">
                        ₺{Number(u.balance || 0).toFixed(2)}
                      </span>
                    </td>

                    <td className="px-4 text-center">
                      <span className="inline-flex items-center gap-1.5 text-white/70">
                        <ShoppingBag className="w-3.5 h-3.5" />
                        {u.order_count ?? 0}
                      </span>
                    </td>

                    <td className="px-4 text-white/50 text-xs">
                      {formatDate(u.created_at)}
                    </td>

                    <td className="px-5">
                      <div className="flex justify-end gap-2">

                        <button
                          onClick={() => openDetails(u)}
                          className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/40 hover:bg-white/5 text-white/70 hover:text-white text-xs px-3 py-2 transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Detay
                        </button>

                        <button
                          onClick={() => {
                            setSelectedUser(u);
                            setShowPassword(true);
                          }}
                          className="inline-flex items-center gap-1.5 border border-white/15 hover:border-[hsl(221,83%,53%)] hover:text-[hsl(221,83%,63%)] text-white/70 text-xs px-3 py-2 transition-colors"
                        >
                          <KeyRound className="w-3.5 h-3.5" />
                          Şifre
                        </button>

                        <button
                          onClick={() => {
                            setSelectedUser(u);
                            setShowBalance(true);
                          }}
                          className="inline-flex items-center gap-1.5 border border-white/15 hover:border-[hsl(142,71%,45%)] hover:text-[hsl(142,71%,55%)] text-white/70 text-xs px-3 py-2 transition-colors"
                        >
                          <Wallet className="w-3.5 h-3.5" />
                          Bakiye
                        </button>

                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showDetails && selectedUser && (
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-white/10 bg-[hsl(0,0%,8%)] shadow-2xl">

              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                    Üye Detayı
                  </div>
                  <h2 className="text-xl font-bold mt-1">
                    {selectedUser.username}
                  </h2>
                </div>

                <button
                  onClick={() => setShowDetails(false)}
                  className="p-2 text-white/50 hover:text-white hover:bg-white/5"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-6">

                <div className="border border-white/10 p-4">
                  <div className="text-[10px] uppercase text-white/40">
                    Bakiye
                  </div>
                  <div className="text-lg font-bold text-[hsl(142,71%,55%)] mt-1">
                    ₺{Number(selectedUser.balance || 0).toFixed(2)}
                  </div>
                </div>

                <div className="border border-white/10 p-4">
                  <div className="text-[10px] uppercase text-white/40">
                    Sipariş
                  </div>
                  <div className="text-lg font-bold mt-1">
                    {selectedUser.order_count ?? 0}
                  </div>
                </div>

                <div className="border border-white/10 p-4">
                  <div className="text-[10px] uppercase text-white/40">
                    Rol
                  </div>
                  <div className="text-lg font-bold mt-1 uppercase">
                    {selectedUser.role || "user"}
                  </div>
                </div>

                <div className="border border-white/10 p-4">
                  <div className="text-[10px] uppercase text-white/40">
                    Kayıt
                  </div>
                  <div className="text-xs text-white/70 mt-2">
                    {formatDate(selectedUser.created_at)}
                  </div>
                </div>

              </div>

              <div className="px-6 pb-6">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3">
                  🧾 Sipariş Geçmişi
                </div>

                {orders.length === 0 ? (
                  <div className="border border-white/10 p-8 text-center text-sm text-white/40">
                    Sipariş geçmişi bulunamadı.
                  </div>
                ) : (
                  <div className="border border-white/10 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="border-b border-white/10 text-white/40">
                        <tr>
                          <th className="text-left p-3">Numara</th>
                          <th className="text-left p-3">Servis</th>
                          <th className="text-left p-3">Ülke</th>
                          <th className="text-right p-3">Tutar</th>
                          <th className="text-left p-3">Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => (
                          <tr
                            key={order.id}
                            className="border-b border-white/5"
                          >
                            <td className="p-3 font-mono">
                              {order.phone || "—"}
                            </td>
                            <td className="p-3">
                              {order.service || "—"}
                            </td>
                            <td className="p-3 text-white/60">
                              {order.country_name || "—"}
                            </td>
                            <td className="p-3 text-right font-mono">
                              ₺{Number(order.price || 0).toFixed(2)}
                            </td>
                            <td className="p-3 text-white/60">
                              {order.status || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

            </div>
          </div>
        )}

        {showPassword && selectedUser && (
          <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-md border border-white/10 bg-[hsl(0,0%,8%)]">

              <div className="flex items-center justify-between p-5 border-b border-white/10">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                    Admin İşlemi
                  </div>
                  <h2 className="font-bold text-lg">
                    Şifre Sıfırla
                  </h2>
                </div>

                <button
                  onClick={() => setShowPassword(false)}
                  className="text-white/50 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 space-y-4">

                <div className="text-sm text-white/60">
                  Kullanıcı:
                  <span className="text-white font-semibold ml-2">
                    {selectedUser.username}
                  </span>
                </div>

                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Yeni şifre"
                  className="w-full bg-black/30 border border-white/10 px-4 py-3 text-sm outline-none focus:border-[hsl(221,83%,53%)]"
                />

                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Yeni şifre tekrar"
                  className="w-full bg-black/30 border border-white/10 px-4 py-3 text-sm outline-none focus:border-[hsl(221,83%,53%)]"
                />

                <button
                  onClick={changePassword}
                  className="w-full bg-[hsl(221,83%,53%)] hover:bg-[hsl(221,83%,58%)] text-white font-bold text-sm py-3 transition-colors"
                >
                  Şifreyi Değiştir
                </button>

              </div>
            </div>
          </div>
        )}

        {showBalance && selectedUser && (
          <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-md border border-white/10 bg-[hsl(0,0%,8%)]">

              <div className="flex items-center justify-between p-5 border-b border-white/10">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                    Bakiye Yönetimi
                  </div>
                  <h2 className="font-bold text-lg">
                    {selectedUser.username}
                  </h2>
                </div>

                <button
                  onClick={() => setShowBalance(false)}
                  className="text-white/50 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 space-y-5">

                <div className="text-center border border-white/10 p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                    Mevcut Bakiye
                  </div>

                  <div className="text-3xl font-bold text-[hsl(142,71%,55%)] mt-2">
                    ₺{Number(selectedUser.balance || 0).toFixed(2)}
                  </div>
                </div>

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={balanceAmount}
                  onChange={(e) => setBalanceAmount(e.target.value)}
                  placeholder="Tutar"
                  className="w-full bg-black/30 border border-white/10 px-4 py-3 text-sm outline-none focus:border-white/30"
                />

                <div className="grid grid-cols-2 gap-3">

                  <button
                    onClick={() => adjustBalance("add")}
                    className="flex items-center justify-center gap-2 bg-[hsl(142,71%,45%)] hover:bg-[hsl(142,71%,50%)] text-white font-bold py-3 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Bakiye Ekle
                  </button>

                  <button
                    onClick={() => adjustBalance("remove")}
                    className="flex items-center justify-center gap-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 font-bold py-3 text-sm"
                  >
                    <Minus className="w-4 h-4" />
                    Bakiye Çıkar
                  </button>

                </div>

              </div>
            </div>
          </div>
        )}

      </div>
    </AdminLayout>
  );
}
